import {
  passenger_model,
  get_pax_field,
  force_mandatory_fields,
  force_mandatory_primary_passenger_fields,
} from "@resources/passenger";
import { get_traveler_birth_range_dates, get_birth_date_constraints } from "@utilities/commons";

/**
 * Build a single field model from API field + passenger_model template.
 * Resolves Italian API field names to English canonical keys via get_pax_field.
 */
export function build_field_model(params) {
  const { field, pax, departure_date, checkin_date } = params;
  const canonical = get_pax_field(field.name);
  const base = {
    ...passenger_model.default,
    ...(passenger_model[canonical] || {}),
  };

  if (base.calendar) {
    base.calendar = { ...base.calendar };
  }
  base.validate = { ...(base.validate || {}) };
  base.name = canonical;

  if (!base.label) {
    base.label = canonical;
  }

  if (field.mandatory && base.validate.required === undefined) {
    base.validate.required = true;
  }

  if (canonical === "birthday") {
    const dates = checkin_date
      ? get_birth_date_constraints({ target_date: checkin_date, adult: pax.age >= 18, age: pax.age })
      : departure_date
        ? get_traveler_birth_range_dates(departure_date, pax.age)
        : null;

    if (dates) {
      base.calendar.min = dates.min;
      base.calendar.max = dates.max;
    }
  }

  if (base.value_default) {
    base.value = base.value_default({ field: field, pax: pax });
  }

  if (field.value) {
    base.value = field.value;
  }

  if (field.readOnly) {
    base.disabled = true;
  }

  return base;
}

/**
 * Build the full passengers model from a quote object.
 * Returns the passengers data structure (does NOT commit to vuex).
 */
export function build_passengers_model(params) {
  const { quote, departure_date, checkin_date } = params;

  const field_name_order = Object.keys(passenger_model).reverse();

  const { compositions = {} } = quote
  const { passenger = [] } = compositions

  const paxs = passenger.map((pax) => {
    const pax_fields = [];
    const is_first_passenger = String(pax.id) === "1";

    pax.details.fields
      .sort(
        (a, b) =>
          field_name_order.indexOf(get_pax_field(b.name)) -
          field_name_order.indexOf(get_pax_field(a.name)),
      )
      .forEach((field) => {
        const canonical = get_pax_field(field.name);

        if (
          is_first_passenger &&
          force_mandatory_primary_passenger_fields.includes(canonical)
        ) {
          field.mandatory = true;
        }

        if (force_mandatory_fields.includes(canonical)) {
          field.mandatory = true;
        }

        if (canonical === "gender") {
          field.mandatory = true;
        }

        if (!field.mandatory && !field.value) {
          return;
        }

        pax_fields.push(build_field_model({ field, pax, departure_date, checkin_date }));
      });

    return {
      id: pax.id,
      passengerType: pax.passengerType,
      age: pax.age,
      details: { fields: pax_fields },
    };
  });

  const result = {
    live_price: quote.live_price,
    id: quote.id,
    passengers: paxs,
  };

  if (quote.bookToken) {
    result.bookToken = quote.bookToken;
  }

  if (quote.senna_book_token) {
    result.senna_book_token = quote.senna_book_token;
  }

  return result;
}

/**
 * Extract departure date from quote services (flight segment).
 */
export function get_departure_date(quote) {
  try {
    return quote.services.flights[0].segments[0].routes[0].departureDateTime;
  } catch (e) {
    return null;
  }
}

/**
 * Dev-only default values for passenger fields.
 */
export function get_dev_field_defaults(name) {
  const def = {
    zip: "60035",
    city: "Jesi",
    municipality: "Jesi",
    fiscal_code: "GRNMHL86L11C615X",
    surname: "Girini",
    birth_city: "Chiaravalle",
    consent: true,
    prefix_phone: "39",
    birthday: "1986-07-11",
    email: "dev@salabam.com",
    address: "via clementina 18",
    nationality: "ITA",
    name: "Michele",
    phone: "3934288350",
    phone_cell_prefix: "",
    province: "AN",
    birth_province: "Ancona",
    gender: "M",
    birth_country: "Italia",
    residence_country: "Italia",
    visa: true,
  };

  return def[name] !== undefined ? def[name] : "";
}
